/* ─────────────────────────────────────────────────────────────────────────────
   PTL → Non-ABD Shipments → EDD Breach Attribution — RTK Query slice.

   Backend: proship_mongo /api/ptl/nabd/* (ROLE_PROSHIPADMIN + PTL_ACCESS).
   Payloads come back directly (no GenericAPIResponse envelope). Errors keep the
   server's status + message so the page can show validation texts verbatim:
   400 = a rule failed (message says which), 409 = someone else changed the case,
   403 = no access / not allowed.

   Breaches are read live from orders, so a row's `id` is its AWB (a breach has no
   case record until someone first acts on it).

   Contract (all JSON):
     GET  summary?tab=U|D           → { tab, tabCounts{U,D}, tiles{NONE,PROZO,CLIENT,EXTERNAL}, matrix[{bucket,counts,total}] }
     POST cases {filter}            → { rows[], total, tabTotal, page, size }
     POST cases/escalation-rows     → [{ awb, orderNo, merchantName, pickupPin, dropPin, pendingWith, assignedMemberName, sentence }]
     POST cases/lookup [awb…]       → { [awb]: { id, breachType, lateDays, owner, closureStatus, locked } }
     GET  cases/:awb                → { row, events[], holdSplit, suggestedFinalOwner, raisedByMemberId, … }
     POST cases/:awb/attribute|reply|close|reopen {action body} → same as GET cases/:awb
     GET  masters                   → { reasons[], replies[], team[], kamMapping[], me, canEditMasters,
                                      limits{minOverrideRemark,maxRemarks,minReplyRemarks,minReopenReason,maxReopenReason} }
     POST cases/export {filter}     → CSV (plain axios, see EddBreachAttribution)
   ──────────────────────────────────────────────────────────────────────────── */
import { createApi } from '@reduxjs/toolkit/query/react'
import { apiConstants, deleteGeneric, get, patch, post, put } from '../common/constants'

const BASE = apiConstants.PTL_NABD

const run = async (promise) => {
  try {
    const res = await promise
    return { data: res?.data }
  } catch (err) {
    const r = err?.response
    return { error: { status: r?.status, data: r?.data, message: r?.data?.message || err?.message || 'Request failed' } }
  }
}

export const ptlNabdApi = createApi({
  reducerPath: 'ptlNabdApi',
  baseQuery: () => ({ data: undefined }), // every endpoint has its own queryFn
  tagTypes: ['Summary', 'Cases', 'Case', 'Lookup', 'Masters'],
  keepUnusedDataFor: 60,
  // pages and dialogs re-fetch data older than 30s when they mount, so a Team Master change made on another page shows up
  refetchOnMountOrArgChange: 30,
  endpoints: (b) => ({
    nabdSummary: b.query({
      queryFn: (tab) => run(get(`${BASE}/summary`, { tab })),
      providesTags: ['Summary'],
    }),
    nabdCases: b.query({
      queryFn: (filter) => run(post(`${BASE}/cases`, filter)),
      providesTags: ['Cases'],
    }),
    nabdLookup: b.query({
      queryFn: (awbs) => run(post(`${BASE}/cases/lookup`, awbs)),
      providesTags: ['Lookup'],
    }),
    nabdCase: b.query({
      queryFn: (id) => run(get(`${BASE}/cases/${encodeURIComponent(id)}`)),
      providesTags: (_r, _e, id) => [{ type: 'Case', id }],
      keepUnusedDataFor: 0,
    }),
    nabdMasters: b.query({
      queryFn: () => run(get(`${BASE}/masters`)),
      providesTags: ['Masters'],
      keepUnusedDataFor: 5 * 60,
    }),

    // ── PTL Team Master (PTL_EDD_ADMIN) ──
    // internal (admin-panel) users {fullName, email} to link a member to a login; server-side search on name or
    // email, at most 20 (GET auth/internal-users?q=)
    internalUsers: b.query({
      queryFn: (q) => run(get(apiConstants.INTERNAL_USERS, { q: q || '' })),
      keepUnusedDataFor: 60,
    }),
    // ── State / City / pincodes from pincode_master (GET geo/states, cities?state=, pincodes?state=&city=) ──
    // options are { value, label }; the PTL Non-ABD list filters deliveryDetails.to_pincode with the pick's pincodes
    geoStates: b.query({
      queryFn: () => run(get(`${apiConstants.GEO}/states`)),
      keepUnusedDataFor: 60 * 60,
    }),
    geoCities: b.query({
      queryFn: (state) => run(get(`${apiConstants.GEO}/cities`, state ? { state } : {})),
      keepUnusedDataFor: 60 * 60,
    }),
    geoPincodes: b.query({
      queryFn: ({ state, city }) => run(get(`${apiConstants.GEO}/pincodes`, { ...(state ? { state } : {}), ...(city ? { city } : {}) })),
      keepUnusedDataFor: 30 * 60,
    }),

    nabdSaveMember: b.mutation({
      queryFn: ({ id, body }) => run(id ? put(`${BASE}/team/${id}`, body) : post(`${BASE}/team`, body)),
      invalidatesTags: ['Masters', 'Cases', 'Case'],
    }),
    nabdSetMemberActive: b.mutation({
      queryFn: ({ id, active }) => run(patch(`${BASE}/team/${id}/active?active=${active}`, {})),
      invalidatesTags: ['Masters'],
    }),
    nabdMapMerchant: b.mutation({
      queryFn: ({ merchantOid, merchantName, kamMemberId }) => run(put(`${BASE}/kam-mapping/${merchantOid}`, { merchantName, kamMemberId })),
      invalidatesTags: ['Masters'],
    }),
    nabdUnmapMerchant: b.mutation({
      queryFn: (merchantOid) => run(deleteGeneric(`${BASE}/kam-mapping/${merchantOid}`)),
      invalidatesTags: ['Masters'],
    }),
    nabdEscalationRows: b.mutation({
      queryFn: (filter) => run(post(`${BASE}/cases/escalation-rows`, filter)),
    }),
    // action: 'attribute' | 'reply' | 'close' | 'reopen'
    nabdAction: b.mutation({
      queryFn: ({ id, action, body }) => run(post(`${BASE}/cases/${encodeURIComponent(id)}/${action}`, body)),
      invalidatesTags: (_r, _e, { id }) => ['Summary', 'Cases', 'Lookup', { type: 'Case', id }],
    }),
  }),
})

export const {
  useNabdSummaryQuery,
  useNabdCasesQuery,
  useNabdLookupQuery,
  useNabdCaseQuery,
  useNabdMastersQuery,
  useNabdEscalationRowsMutation,
  useNabdActionMutation,
  useInternalUsersQuery,
  useNabdSaveMemberMutation,
  useNabdSetMemberActiveMutation,
  useNabdMapMerchantMutation,
  useNabdUnmapMerchantMutation,
  useGeoStatesQuery,
  useGeoCitiesQuery,
  useGeoPincodesQuery,
  useLazyGeoPincodesQuery,
} = ptlNabdApi
